import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Edit2, Trash2, X, HeartPulse, CheckCircle2, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { AdminService, apiFetch } from '../../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('module_id', '1');
  formData.append('pathLocation', 'HOMECARE_CATEGORY_IMAGES/');
  const response = await apiFetch(`${API_BASE_URL}/admin/awsS3FileUpload`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) throw new Error(`Upload failed: ${response.statusText}`);
  const json = await response.json();
  return json.privatePathUrl || json.url || json.filePath || '';
};

const HomeCareCategoryMaster = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', icon_filename: '', sort_order: 0 });
  const [iconPreview, setIconPreview] = useState(null);
  const [iconUploading, setIconUploading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  const iconInputRef = useRef(null);

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 4000);
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await AdminService.getAllHomeCareCategories();
      if (response && response.status === 1 && Array.isArray(response.data)) {
        setItems(response.data);
      } else {
        setItems([]);
      }
    } catch (err) {
      showAlert('error', `Failed to load home care categories: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const resetForm = () => {
    setFormData({ name: '', icon_filename: '', sort_order: 0 });
    setIconPreview(null);
  };

  const handleOpenAdd = () => {
    setEditingItem(null);
    resetForm();
    setModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name || '', icon_filename: item.icon_filename || '', sort_order: item.sort_order || 0 });
    setIconPreview(item.icon_filename || null);
    setModalOpen(true);
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIconPreview(URL.createObjectURL(file));
    setIconUploading(true);
    try {
      const url = await uploadImage(file);
      setFormData(prev => ({ ...prev, icon_filename: url }));
    } catch (err) {
      showAlert('error', `Icon upload failed: ${err.message}`);
      setIconPreview(formData.icon_filename || null);
    } finally {
      setIconUploading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    try {
      const response = await AdminService.deleteHomeCareCategory(id);
      if (response && response.status === 1) {
        showAlert('success', response.msg || 'Home Care category deleted successfully');
        fetchItems();
      } else {
        showAlert('error', response?.msg || 'Cannot delete category');
      }
    } catch (err) {
      showAlert('error', err.message || 'Error deleting category');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showAlert('error', 'Category name is required');
      return;
    }
    if (!formData.icon_filename) {
      showAlert('error', 'Icon image is required');
      return;
    }
    if (iconUploading) {
      showAlert('error', 'Please wait for the icon upload to finish');
      return;
    }

    setActionLoading(true);
    try {
      let response;
      if (editingItem) {
        response = await AdminService.updateHomeCareCategory(editingItem.id, formData);
      } else {
        response = await AdminService.addHomeCareCategory(formData);
      }
      if (response && response.status === 1) {
        showAlert('success', editingItem ? 'Home Care category updated successfully' : 'Home Care category added successfully');
        setModalOpen(false);
        resetForm();
        fetchItems();
      } else {
        showAlert('error', response?.msg || 'Failed to save category');
      }
    } catch (err) {
      showAlert('error', err.message || 'Error saving category');
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = items.filter((item) =>
    (item.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="content-section">
      <div className="content-header">
        <div>
          <h1>Home Care Category Master</h1>
          <p className="text-xs text-slate-400 font-medium mt-1">Manage home care categories shown on the patient app</p>
        </div>
        <div className="header-actions">
          <div className="search-container">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="create-btn" onClick={handleOpenAdd}>
            <Plus size={18} /> Add Category
          </button>
        </div>
      </div>

      {alert.show && (
        <div className={`mb-6 p-4 rounded-xl text-sm font-semibold flex items-center gap-3 ${alert.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {alert.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span>{alert.message}</span>
        </div>
      )}

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <HeartPulse size={48} className="mx-auto mb-3 text-slate-300" />
          <h3>No Home Care Categories Found</h3>
          <p>Click "Add Category" to create a new home care category.</p>
        </div>
      ) : (
        <div className="cards-grid">
          {filtered.map((item) => (
            <div key={item.id} className="admin-mgmt-card group">
              <div className="admin-mgmt-card-header">
                <div className="flex items-center gap-3.5">
                  {item.icon_filename ? (
                    <img
                      src={item.icon_filename}
                      alt={item.name}
                      className="w-12 h-12 rounded-xl object-contain bg-slate-50 p-1.5 border border-slate-100 shadow-sm"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center font-bold text-lg border border-blue-100 shadow-sm">
                      <HeartPulse size={22} />
                    </div>
                  )}
                  <div>
                    <h3 className="text-base font-bold text-slate-800 leading-snug">{item.name}</h3>
                    <span className="text-[11px] text-slate-400 font-medium">ID: #{item.id}</span>
                  </div>
                </div>
                <span className="status-pill approved">
                  <span className="dot" />
                  Active
                </span>
              </div>

              <div className="admin-mgmt-card-footer flex justify-between items-center pt-3 border-t border-slate-100">
                <span className="text-xs text-slate-400">Home Care Category</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleOpenEdit(item)} className="p-2 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-all" title="Edit Category">
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => handleDelete(item.id, item.name)} className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition-all" title="Delete Category">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 relative animate-fadeIn" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <button onClick={() => { setModalOpen(false); resetForm(); }} className="absolute top-4 right-4 p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
              <X size={18} />
            </button>

            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100 shadow-sm">
                <HeartPulse size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800 leading-tight">
                  {editingItem ? 'Edit Category' : 'Add New Category'}
                </h3>
                <p className="text-xs text-slate-500 font-medium">Home Care category</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Medicines, Baby Care"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 font-medium"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Icon Image <span className="text-red-500">*</span>
                </label>
                <div
                  onClick={() => iconInputRef.current?.click()}
                  className="cursor-pointer border-2 border-dashed rounded-xl p-4 flex items-center gap-4 transition-all hover:border-blue-400 hover:bg-blue-50"
                  style={{ borderColor: iconPreview ? '#2563eb' : '#e2e8f0' }}
                >
                  {iconPreview ? (
                    <img src={iconPreview} alt="Icon preview" className="w-16 h-16 rounded-xl object-contain bg-slate-50 border border-slate-200 p-1" />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <ImageIcon size={24} className="text-slate-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-bold text-slate-700">
                      {iconUploading ? 'Uploading...' : iconPreview ? 'Click to change icon' : 'Click to upload icon'}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">PNG, JPG, SVG recommended · Max 10MB</p>
                    {iconUploading && (
                      <div className="mt-1.5 h-1.5 w-32 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: '60%' }} />
                      </div>
                    )}
                  </div>
                </div>
                <input ref={iconInputRef} type="file" accept="image/*" onChange={handleIconUpload} className="hidden" />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Sort Order <span className="text-xs text-slate-400 normal-case font-medium">(Optional)</span>
                </label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: Number(e.target.value) || 0 })}
                  placeholder="0"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 font-medium"
                />
              </div>

              <div className="pt-2 flex items-center justify-end gap-2.5">
                <button type="button" onClick={() => { setModalOpen(false); resetForm(); }} className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-bold transition-all">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading || iconUploading}
                  className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-all shadow-md shadow-blue-600/20 disabled:opacity-50"
                >
                  {actionLoading ? 'Saving...' : editingItem ? 'Update Category' : 'Add Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeCareCategoryMaster;
